import { ICON_NAMES } from '@repo/design-system';
import type { FieldAsideTone, IconName } from '@repo/design-system';
import type { AdminTwin, FieldAvailability } from '@repo/frontend-types';

/**
 * The create form's pure half (`twin-details-page-create`, frame 01) — every decision the form
 * makes about what to show, kept out of the component so each is testable without a DOM.
 *
 * **The BID rules are the server's.** The client upper-cases what is typed and strips a pasted
 * organisation prefix; everything else — length, characters, uniqueness — is the server's answer,
 * shown in its own words (`checkTwinAvailability`, `createTwin`). No regex of the BID rule lives in
 * the browser, so the two can never disagree.
 */

/** The three fields of the form, in the order they appear (and take focus). */
export type CreateField = 'name' | 'bid' | 'type';

export const CREATE_FIELDS: readonly CreateField[] = ['name', 'bid', 'type'];

/**
 * What the BID input holds: the part after the organisation's prefix, upper-cased. A pasted value
 * that starts with the prefix — in any case — has it stripped once, so pasting `pul-connect` into a
 * `PUL-` field gives `CONNECT`, not `PUL-CONNECT` twice over. Nothing else is rewritten: a hyphen or
 * a thirteenth character stays, and the server's answer names it.
 */
export const normaliseBidPart = (raw: string, orgBid: string): string => {
  const upper = raw.toUpperCase();
  const prefix = `${orgBid}-`.toUpperCase();
  return upper.startsWith(prefix) ? upper.slice(prefix.length) : upper;
};

/** A name as sent: no line breaks (the server refuses them), and no surrounding spaces. */
export const stripLineBreaks = (raw: string): string => raw.replace(/[\r\n]+/g, ' ');

export const nameForCreate = (raw: string): string => stripLineBreaks(raw).trim();

/** The latest check for one field: not asked, waiting on one, or the server's answer (`undefined`: couldn't check). */
export type CheckState = 'idle' | 'pending' | { answer: FieldAvailability | undefined };

export interface FieldView {
  aside: { text: string; tone: FieldAsideTone } | null;
  error: string | null;
}

export const COULD_NOT_CHECK = 'Couldn’t check — Create Twin checks again';

/**
 * The BID field's aside and error for the latest check (spec R4.2):
 *
 * | Check | Aside | Error |
 * | --- | --- | --- |
 * | empty / idle | — | — |
 * | pending | *Checking…* | — |
 * | `available` | *Available*, success | — |
 * | `taken` / `invalid` | — | the server's message |
 * | `undefined` | *Couldn't check — Create Twin checks again* | — |
 *
 * ⚠ `undefined` is NEVER *Available*: that would claim a BID nobody checked.
 */
export const bidAside = (check: CheckState, value: string): FieldView => {
  if (value === '' || check === 'idle') return { aside: null, error: null };
  if (check === 'pending') return { aside: { text: 'Checking…', tone: 'neutral' }, error: null };
  const { answer } = check;
  if (answer === undefined)
    return { aside: { text: COULD_NOT_CHECK, tone: 'neutral' }, error: null };
  if (answer.status === 'available')
    return { aside: { text: 'Available', tone: 'success' }, error: null };
  return { aside: null, error: answer.message };
};

/** The name's error from its latest check — it has no *Available* state, because frame 01 draws none. */
export const nameError = (check: CheckState): string | null =>
  typeof check === 'object' && check.answer && check.answer.status !== 'available'
    ? check.answer.message
    : null;

/** Server problems by the form's fields; any field the form does not have goes to `form`, verbatim. */
export interface ProblemsByField {
  name?: string;
  bid?: string;
  type?: string;
  form: string[];
}

const SERVER_FIELD: Readonly<Record<string, CreateField>> = {
  name: 'name',
  bid: 'bid',
  twin_template_id: 'type',
  template: 'type',
};

export const problemsByField = (
  problems: ReadonlyArray<{ field: string; message: string }>,
): ProblemsByField => {
  const out: ProblemsByField = { form: [] };
  for (const { field, message } of problems) {
    const mine = Object.prototype.hasOwnProperty.call(SERVER_FIELD, field)
      ? SERVER_FIELD[field]
      : undefined;
    if (!mine) {
      out.form.push(message);
      continue;
    }
    out[mine] = out[mine] ? `${out[mine]} ${message}` : message;
  }
  return out;
};

/** What the form holds when *Create Twin* is pressed. */
export interface CreateValues {
  name: string;
  bidPart: string;
  typeId: number | null;
}

const MISSING: Readonly<Record<CreateField, string>> = {
  name: 'Enter a name',
  bid: 'Enter the rest of the BID',
  type: 'Choose a type',
};

/** The fields still empty, in form order, with the sentence each shows (spec R3.4). */
export const missingFields = (
  values: CreateValues,
): Array<{ field: CreateField; message: string }> => {
  const missing: CreateField[] = [];
  if (nameForCreate(values.name) === '') missing.push('name');
  if (values.bidPart === '') missing.push('bid');
  if (values.typeId === null) missing.push('type');
  return missing.map(field => ({ field, message: MISSING[field] }));
};

/**
 * Whether a twin found by its BID after a create of unknown outcome IS that create (Failure States,
 * row 3): the same organisation, the same BID, and the same name ignoring case and surrounding
 * spaces — the server stores the name normalised, and refuses names that differ only in case.
 */
export const isSameCreate = (
  twin: Pick<AdminTwin, 'organisation_id' | 'bid' | 'name'>,
  request: { organisationId: number; bid: string; name: string },
): boolean =>
  twin.organisation_id === request.organisationId &&
  twin.bid === request.bid &&
  (twin.name ?? '').trim().toLowerCase() === request.name.trim().toLowerCase();

/** The type cards' column count: one per type, as card 03 allows — two to five. */
export const typeColumns = (count: number): 2 | 3 | 4 | 5 =>
  Math.min(5, Math.max(2, count)) as 2 | 3 | 4 | 5;

const LABEL: Readonly<Record<CreateField, string>> = { name: 'Name', bid: 'BID', type: 'Type' };

/** *Check the BID and Type* — a refused create's one-sentence summary, naming the fields in error. */
export const refusedSummary = (fields: readonly CreateField[]): string => {
  const names = CREATE_FIELDS.filter(f => fields.includes(f)).map(f => LABEL[f]);
  if (names.length === 0) return '';
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Check the ${list}`;
};

/**
 * Whether a types-table `icon` is a glyph the design system's kit draws. The table is edited by
 * hand in AdminDB, so a name may not be one; read by the create form and Twin details' identity line.
 */
export const isIconName = (name: string): name is IconName =>
  (ICON_NAMES as readonly string[]).includes(name);
